import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IonContent, IonPage, IonHeader, IonToolbar, IonTitle,
  IonToast, IonLoading
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import {
  LogIn, UserPlus, ArrowRight, Eye, EyeOff, User,
  Mail, Lock, Shield, Fingerprint, Loader2, Check,
  Headphones, ChevronRight, Star, Lock as LockIcon,
  Sparkles, AlertCircle, KeyRound
} from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { biometricAuth } from '../services/socialAuth.service';
import LazyImage from '../components/LazyImage';

// ============================================================
// PRODUCTION CONFIG — Environment Variables
// ============================================================
const GOOGLE_WEB_CLIENT_ID = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || 
  '206594127331-mpf3fi6em5b0r1jujvuo09htu5ver1q5.apps.googleusercontent.com';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID || 'com.nurwallet.app';
const APPLE_REDIRECT_URL = import.meta.env.VITE_APPLE_REDIRECT_URL || 'https://nurwallet.app/callback';

// ============================================================
// SECURITY CONSTANTS
// ============================================================
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const PASSWORD_MIN_LENGTH = 8;
const RATE_LIMIT_KEY = 'nurwallet_login_attempts';
const RATE_LIMIT_LOCKOUT_KEY = 'nurwallet_lockout_until';

// ============================================================
// ERROR BOUNDARY — Production crash protection
// ============================================================
class LoginErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // In production, send to crash reporting service
    if (import.meta.env.PROD) {
      // TODO: Send to Sentry/Firebase Crashlytics
      // Example: Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          textAlign: 'center',
          background: '#FAF8F0'
        }}>
          <AlertCircle size={48} color="#EF4444" />
          <h2 style={{ margin: '16px 0 8px', color: '#1F2937', fontSize: '20px' }}>
            Something went wrong
          </h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            Please restart the app. If this persists, contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#1B5A40',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Restart App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================
// TRUST BADGE ICON — uses imported LazyImage
// ============================================================
const TrustBadgeIcon: React.FC<{ src: string; alt: string }> = React.memo(({ src, alt }) => (
  <div style={{ width: '40px', height: '40px', margin: '0 auto 8px' }}>
    <LazyImage
      src={src}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  </div>
));

// ============================================================
// INPUT VALIDATION UTILITIES
// ============================================================
const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email.trim().toLowerCase());
};

const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`At least ${PASSWORD_MIN_LENGTH} characters`);
  }
  if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('One number');
  if (!/[!@#$%^&*]/.test(password)) errors.push('One special character (!@#$%^&*)');
  return { valid: errors.length === 0, errors };
};

const sanitizeInput = (input: string): string => {
  // Basic XSS prevention
  return input
    .replace(/[<>]/g, '')
    .trim()
    .slice(0, 255); // Max length limit
};

// ============================================================
// RATE LIMITING
// ============================================================
const getRateLimitData = () => {
  try {
    const attempts = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
    const lockoutUntil = parseInt(localStorage.getItem(RATE_LIMIT_LOCKOUT_KEY) || '0', 10);
    return { attempts, lockoutUntil };
  } catch {
    return { attempts: 0, lockoutUntil: 0 };
  }
};

const recordFailedAttempt = () => {
  try {
    const { attempts } = getRateLimitData();
    const newAttempts = attempts + 1;
    localStorage.setItem(RATE_LIMIT_KEY, newAttempts.toString());
    if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
      localStorage.setItem(RATE_LIMIT_LOCKOUT_KEY, (Date.now() + LOCKOUT_DURATION_MS).toString());
    }
  } catch { /* localStorage not available */ }
};

const resetRateLimit = () => {
  try {
    localStorage.removeItem(RATE_LIMIT_KEY);
    localStorage.removeItem(RATE_LIMIT_LOCKOUT_KEY);
  } catch { /* localStorage not available */ }
};

const isRateLimited = (): { limited: boolean; remainingMs: number } => {
  const { lockoutUntil } = getRateLimitData();
  if (lockoutUntil > Date.now()) {
    return { limited: true, remainingMs: lockoutUntil - Date.now() };
  }
  return { limited: false, remainingMs: 0 };
};

// ============================================================
// PASSWORD HASHING — bcryptjs via dynamic import
// ============================================================
const hashPassword = async (password: string): Promise<string> => {
  const bcrypt = await import('bcryptjs');
  return bcrypt.hash(password, 12); // 12 rounds = ~250ms
};

const verifyPassword = async (password: string, hash: string): Promise<boolean> => {
  const bcrypt = await import('bcryptjs');
  return bcrypt.compare(password, hash);
};

// ============================================================
// SECURE TOKEN GENERATION
// ============================================================
const generateSecureToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// ============================================================
// PRODUCTION LOGGER — No console.log in production
// ============================================================
const logger = {
  debug: (...args: any[]) => { if (!import.meta.env.PROD) console.debug(...args); },
  info: (...args: any[]) => { if (!import.meta.env.PROD) console.info(...args); },
  warn: (...args: any[]) => { if (!import.meta.env.PROD) console.warn(...args); },
  error: (message: string, error?: any) => {
    // Always log errors, but in production send to crash reporting
    if (import.meta.env.PROD) {
      // TODO: Send to Sentry/Firebase Crashlytics
      // Sentry.captureException(error || new Error(message));
    } else {
      console.error(message, error);
    }
  }
};

// ============================================================
// LAZY SOCIAL LOGIN INIT — initialized only when user presses
// the Google/Apple button, not on every app start
// ============================================================
let socialLoginReady = false;
const ensureSocialLoginReady = async () => {
  if (socialLoginReady) return;
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  await SocialLogin.initialize({
    google: {
      webClientId: GOOGLE_WEB_CLIENT_ID,
      iOSClientId: '206594127331-lld1elj6dmbjp13u2ksudel0a6btqu7o.apps.googleusercontent.com',
      mode: 'online',
    },
    apple: {
      clientId: APPLE_CLIENT_ID,
      redirectUrl: APPLE_REDIRECT_URL,
    },
  });
  socialLoginReady = true;
};

// ============================================================
// MAIN LOGIN SCREEN
// ============================================================
const LoginScreen: React.FC = () => {
  const history = useHistory();
  const { setUser, isAuthenticated, isHydrated } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [lockoutMessage, setLockoutMessage] = useState('');

  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<1 | 2>(1); // 1=email, 2=new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');

  // Biometric states
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState<string>('none');
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  // ============================================================
  // MOUNT GUARD
  // ============================================================
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ============================================================
  // AUTH GUARD — redirect to /home if session already valid
  // Handles cold restart where Capacitor Preferences has a live session
  // ============================================================
  useEffect(() => {
    if (isHydrated && isAuthenticated) {
      history.replace('/home');
    }
  }, [isHydrated, isAuthenticated, history]);

  // ============================================================
  // CRITICAL: Reset rate limits on mount
  // NOTE: We no longer clear __NURWALLET_USER_ID__ here. The global
  // ID is cleared by authStore.logout(). Clearing it unconditionally
  // could corrupt an active session if the login screen remounts.
  // ============================================================
  useEffect(() => {
    resetRateLimit();
    logger.info('[LoginScreen] Mount');
  }, []);

  // ============================================================
  // RATE LIMIT CHECK
  // ============================================================
  useEffect(() => {
    const { limited, remainingMs } = isRateLimited();
    if (limited) {
      const minutes = Math.ceil(remainingMs / 60000);
      setLockoutMessage(`Too many attempts. Try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
    } else {
      setLockoutMessage('');
    }
  }, []);

  // ============================================================
  // BIOMETRIC — Check once only using ref (FIXED)
  // Prevents re-check loop when component re-mounts
  // ============================================================
  const hasCheckedBiometric = useRef(false);

  useEffect(() => {
    if (hasCheckedBiometric.current) return;
    hasCheckedBiometric.current = true;

    let cancelled = false;

    const checkBiometricStatus = async () => {
      try {
        const available = await biometricAuth.isAvailable();
        if (cancelled || !isMounted.current) return;

        setBiometricAvailable(available);
        if (available) {
          const type = await biometricAuth.getBiometryType();
          const enabled = await biometricAuth.isBiometricEnabled();
          if (!isMounted.current) return;

          setBiometricType(type);
          setBiometricEnabled(enabled);
        }
      } catch (e) {
        logger.warn('Biometric check failed:', e);
        if (isMounted.current) {
          setBiometricAvailable(false);
          setBiometricEnabled(false);
        }
      }
    };

    const timer = setTimeout(checkBiometricStatus, 100);
    return () => { 
      cancelled = true; 
      clearTimeout(timer); 
    };
  }, []);

  // ============================================================
  // STABLE HANDLERS
  // ============================================================
  const showError = useCallback((msg: string) => {
    if (!isMounted.current) return;
    setToastMessage(msg);
    setShowToast(true);
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(sanitizeInput(e.target.value));
    setValidationErrors([]);
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setValidationErrors([]);
  }, []);

  const handleFullNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(sanitizeInput(e.target.value));
  }, []);

  const togglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleRegister = useCallback(() => {
    setIsRegistering(prev => !prev);
    setEmail('');
    setPassword('');
    setFullName('');
    setValidationErrors([]);
    setLockoutMessage('');
  }, []);

  const toggleRememberMe = useCallback(() => {
    setRememberMe(prev => !prev);
  }, []);

  // ============================================================
  // LOGIN — Production hardened
  // ============================================================
  const handleLogin = useCallback(async () => {
    // Rate limit check
    const { limited, remainingMs } = isRateLimited();
    if (limited) {
      const minutes = Math.ceil(remainingMs / 60000);
      showError(`Too many attempts. Try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
      return;
    }

    // Validation
    if (!email.trim() || !password.trim()) {
      showError('Please enter email and password');
      return;
    }

    if (!validateEmail(email)) {
      showError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await capacitorDbService.init();
      const user = await capacitorDbService.getUserByEmail(email.trim().toLowerCase());

      if (!user) {
        recordFailedAttempt();
        showError('Invalid email or password');
        setIsLoading(false);
        return;
      }

      // Verify password with bcrypt
      let passwordValid = false;
      if (user.password.startsWith('$2')) {
        // Hashed password
        passwordValid = await verifyPassword(password, user.password);
      } else {
        // Legacy plaintext — migrate to hash
        passwordValid = user.password === password;
        if (passwordValid) {
          const newHash = await hashPassword(password);
          await capacitorDbService.updateUser(user.id, { password: newHash });
          user.password = newHash;
        }
      }

      if (!passwordValid) {
        recordFailedAttempt();
        const { attempts } = getRateLimitData();
        const remaining = MAX_LOGIN_ATTEMPTS - attempts;
        showError(
          remaining > 0 
            ? `Invalid email or password. ${remaining} attempts remaining.`
            : 'Account locked. Too many failed attempts.'
        );
        setIsLoading(false);
        return;
      }

      // Success — reset rate limit
      resetRateLimit();

      // Generate secure session token
      const sessionToken = generateSecureToken();
      await capacitorDbService.updateUser(user.id, { 
        session_token: sessionToken,
        last_login: new Date().toISOString()
      });

      // Store token securely (Preferences API is encrypted on iOS)
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: 'nurwallet_session', value: sessionToken });

      // Set global user ID BEFORE store update
      (window as any).__NURWALLET_USER_ID__ = user.id;
      // CRITICAL: setUser is now async on iOS (persists to Capacitor Preferences)
      console.log('[LoginScreen] handleLogin: calling setUser for user', user.id);
      await setUser({ ...user, sessionToken }, false);
      console.log('[LoginScreen] handleLogin: setUser completed, navigating to /home');

      // Biometric prompt
      if (biometricAvailable && !biometricEnabled && rememberMe) {
        const enableBio = window.confirm('Enable Face ID / Fingerprint for faster login?');
        if (enableBio) {
          // Store encrypted token, not plaintext password
          await biometricAuth.enableBiometricLogin(email, sessionToken);
          if (isMounted.current) setBiometricEnabled(true);
        }
      }

      showError('✅ Welcome back, ' + (user.fullName || user.full_name || 'User') + '!');
      setTimeout(() => {
        if (isMounted.current) {
          console.log('[LoginScreen] handleLogin: executing history.replace(/home)');
          history.replace('/home');
        }
      }, 1000);
    } catch (error: any) {
      logger.error('Login failed', error);
      showError('Login failed. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [email, password, biometricAvailable, biometricEnabled, rememberMe, showError, history, setUser]);

  // ============================================================
  // REGISTER — Production hardened
  // ============================================================
  const handleRegister = useCallback(async () => {
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedName = sanitizeInput(fullName);

    if (!sanitizedEmail || !password.trim() || !sanitizedName) {
      showError('Please fill in all fields');
      return;
    }

    if (!validateEmail(sanitizedEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      setValidationErrors(passwordCheck.errors);
      showError('Password does not meet requirements');
      return;
    }

    setIsLoading(true);
    try {
      await capacitorDbService.init();

      const existing = await capacitorDbService.getUserByEmail(sanitizedEmail.toLowerCase());
      if (existing) {
        showError('Email already registered. Please login instead.');
        setIsLoading(false);
        return;
      }

      // Hash password before storage
      const passwordHash = await hashPassword(password);
      const sessionToken = generateSecureToken();

      const newUser = await capacitorDbService.createUser({
        email: sanitizedEmail.toLowerCase(),
        password: passwordHash,
        full_name: sanitizedName,
        family_status: 'SINGLE',
        session_token: sessionToken,
        created_at: new Date().toISOString(),
      });

      // Store session
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: 'nurwallet_session', value: sessionToken });

      (window as any).__NURWALLET_USER_ID__ = newUser.id;
      // CRITICAL: setUser is now async on iOS (persists to Capacitor Preferences)
      console.log('[LoginScreen] handleRegister: calling setUser for user', newUser.id);
      await setUser({ ...newUser, sessionToken }, false);
      console.log('[LoginScreen] handleRegister: setUser completed, navigating to /home');

      showError('✅ Account created! Welcome, ' + sanitizedName);
      setTimeout(() => {
        if (isMounted.current) {
          console.log('[LoginScreen] handleRegister: executing history.replace(/home)');
          history.replace('/home');
        }
      }, 1000);
    } catch (error: any) {
      logger.error('Registration failed', error);
      showError('Registration failed. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [email, password, fullName, showError, history, setUser]);

  // ============================================================
  // GUEST LOGIN
  // ============================================================
  const handleGuestLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      await capacitorDbService.init();

      let guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');

      if (!guestUser) {
        const sessionToken = generateSecureToken();
        guestUser = await capacitorDbService.createUser({
          email: 'guest@nurwallet.local',
          password: await hashPassword('guest_' + Date.now()),
          full_name: 'Guest User',
          family_status: 'SINGLE',
          session_token: sessionToken,
        });
      }

      (window as any).__NURWALLET_USER_ID__ = guestUser.id;
      // CRITICAL: setUser is now async on iOS (persists to Capacitor Preferences)
      await setUser(guestUser, true);

      showError('✅ Continuing as Guest');
      setTimeout(() => {
        if (isMounted.current) history.replace('/home');
      }, 1000);
    } catch (error: any) {
      logger.error('Guest login failed', error);
      showError('Guest login failed. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [showError, history, setUser]);

  // ============================================================
  // GOOGLE SIGN-IN
  // ============================================================
  const handleGoogleSignIn = useCallback(async () => {
    setIsLoading(true);
    try {
      await ensureSocialLoginReady();
      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const result = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
          filterByAuthorizedAccounts: false,
        },
      });

      // Cast to any: CapGo plugin types don't reflect Android runtime shape
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const googleResult = result.result as any;
      if (!googleResult || (!googleResult.idToken && !googleResult.accessToken)) {
        showError('Google login was cancelled or failed. Please try again.');
        setIsLoading(false);
        return;
      }

      const profile = googleResult.profile;
      const email = sanitizeInput(profile?.email || '');

      if (!email || !validateEmail(email)) {
        showError('Invalid email from Google. Please try again.');
        setIsLoading(false);
        return;
      }

      await capacitorDbService.init();
      let user = await capacitorDbService.getUserByEmail(email.toLowerCase());

      const sessionToken = generateSecureToken();

      if (!user) {
        user = await capacitorDbService.createUser({
          email: email.toLowerCase(),
          password: await hashPassword('google_oauth_' + Date.now() + generateSecureToken()),
          fullName: sanitizeInput(profile?.name || 'Google User'),
          familyStatus: 'SINGLE',
        });
      } else {
        try {
          await capacitorDbService.updateUser(user.id, {
            last_login: new Date().toISOString()
          });
        } catch (e) { /* ignore missing column */ }
      }

      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: 'nurwallet_session', value: sessionToken });
      await Preferences.set({ key: 'accessToken', value: sessionToken });
      await Preferences.set({ key: 'current_user_id', value: user.id });
      await Preferences.set({ key: 'user', value: JSON.stringify(user) });

      (window as any).__NURWALLET_USER_ID__ = user.id;
      // CRITICAL: setUser is now async on iOS (persists to Capacitor Preferences)
      await setUser({ ...user, sessionToken }, false);

      if (isMounted.current) {
        setToastMessage('Welcome, ' + (user.fullName || 'User') + '!');
        setShowToast(true);
      }
      setTimeout(() => {
        if (isMounted.current) history.replace('/home');
      }, 1000);
    } catch (error: any) {
      logger.error('Google login failed', error);
      const msg = error?.message || '';
      if (msg.includes('cancel') || msg.includes('dismiss') || msg.includes('popup')) {
        // User cancelled - don't show error
      } else if (msg.includes('10') || msg.includes('DEVELOPER_ERROR')) {
        showError('Google Sign-In config error. Check Android SHA-1 in Google Cloud Console.');
      } else if (msg.includes('12500') || msg.includes('12501')) {
        showError('Google Sign-In failed. Please check your Google account and try again.');
      } else {
        showError('Google login failed: ' + (msg || 'Unknown error'));
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [showError, history, setUser]);

  // ============================================================
  // APPLE SIGN-IN
  // ============================================================
  const handleAppleSignIn = useCallback(async () => {
    setIsLoading(true);
    try {
      await ensureSocialLoginReady();
      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const result = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name'],
        },
      });

      if (result.result) {
        const profile = result.result.profile;
        const email = sanitizeInput(profile?.email || `apple_${Date.now()}@privaterelay.appleid.com`);

        await capacitorDbService.init();
        let user = await capacitorDbService.getUserByEmail(email.toLowerCase());

        const sessionToken = generateSecureToken();
        const fullName = sanitizeInput(
          (profile?.givenName && profile?.familyName)
            ? `${profile.givenName} ${profile.familyName}`
            : (profile?.givenName || 'Apple User')
        );

        if (!user) {
          user = await capacitorDbService.createUser({
            email: email.toLowerCase(),
            password: await hashPassword('apple_oauth_' + Date.now() + generateSecureToken()),
            fullName: fullName,
            familyStatus: 'SINGLE',
          });
        } else {
          try {
            await capacitorDbService.updateUser(user.id, {
              last_login: new Date().toISOString()
            });
          } catch (e) { /* ignore missing column */ }
        }

        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({ key: 'nurwallet_session', value: sessionToken });
        await Preferences.set({ key: 'accessToken', value: sessionToken });
        await Preferences.set({ key: 'current_user_id', value: user.id });
        await Preferences.set({ key: 'user', value: JSON.stringify(user) });

        (window as any).__NURWALLET_USER_ID__ = user.id;
        // CRITICAL: setUser is now async on iOS (persists to Capacitor Preferences)
        await setUser({ ...user, sessionToken }, false);

        if (isMounted.current) {
          setToastMessage('Welcome, ' + (user.fullName || fullName || 'User') + '!');
          setShowToast(true);
        }
        setTimeout(() => {
          if (isMounted.current) history.replace('/home');
        }, 1000);
      } else {
        showError('Apple login failed. Please try again.');
      }
    } catch (error: any) {
      logger.error('Apple login failed', error);
      if (!error.message?.includes('cancel') && !error.message?.includes('dismiss')) {
        showError('Apple login failed. Please try again.');
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [showError, history, setUser]);

  // ============================================================
  // BIOMETRIC LOGIN — Uses secure token via password field
  // NOTE: biometricAuth returns { success, email, password, error }
  // We store the session token in the 'password' field for backward compat
  // ============================================================
  const handleBiometricLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await biometricAuth.authenticateWithBiometric();
      // biomeAuth returns: { success, email, password, error }
      // The 'password' field contains the stored credential (token or password)
      if (result.success && result.email && result.password) {
        setEmail(result.email);

        await capacitorDbService.init();
        const user = await capacitorDbService.getUserByEmail(result.email);

        if (!user) {
          showError('User not found. Please login manually.');
          await biometricAuth.disableBiometricLogin();
          if (isMounted.current) setBiometricEnabled(false);
          setIsLoading(false);
          return;
        }

        // Check if stored credential is a session token (new) or password (legacy)
        const storedCredential = result.password;
        const isToken = storedCredential.length === 64; // Our tokens are 64 hex chars

        if (isToken) {
          // Modern token-based biometric
          if (user.session_token !== storedCredential) {
            showError('Session expired. Please login manually.');
            await biometricAuth.disableBiometricLogin();
            if (isMounted.current) setBiometricEnabled(false);
            setIsLoading(false);
            return;
          }

          // Generate new token for this session
          const newToken = generateSecureToken();
          await capacitorDbService.updateUser(user.id, { session_token: newToken });

          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.set({ key: 'nurwallet_session', value: newToken });

          (window as any).__NURWALLET_USER_ID__ = user.id;
          await setUser({ ...user, sessionToken: newToken }, false);
        } else {
          // Legacy password-based biometric
          // Verify password
          let passwordValid = false;
          if (user.password.startsWith('$2')) {
            passwordValid = await verifyPassword(storedCredential, user.password);
          } else {
            passwordValid = user.password === storedCredential;
          }

          if (!passwordValid) {
            showError('Stored password invalid. Please login manually.');
            await biometricAuth.disableBiometricLogin();
            if (isMounted.current) setBiometricEnabled(false);
            setIsLoading(false);
            return;
          }

          // Migrate to token-based
          const newToken = generateSecureToken();
          await capacitorDbService.updateUser(user.id, { session_token: newToken });

          // Re-enable biometric with token for next time
          await biometricAuth.enableBiometricLogin(result.email, newToken);

          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.set({ key: 'nurwallet_session', value: newToken });

          (window as any).__NURWALLET_USER_ID__ = user.id;
          await setUser({ ...user, sessionToken: newToken }, false);
        }

        showError('✅ Welcome back, ' + user.full_name + '!');
        setTimeout(() => {
          if (isMounted.current) history.replace('/home');
        }, 1000);
      } else {
        showError(result.error || 'Biometric authentication failed');
      }
    } catch (error) {
      logger.error('Biometric login failed', error);
      showError('Biometric login failed. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [showError, history, setUser]);

  const getBiometricLabel = useCallback(() => {
    if (biometricType === 'faceId') return 'Face ID';
    if (biometricType === 'touchId') return 'Touch ID';
    if (biometricType === 'fingerprint') return 'Fingerprint';
    return 'Biometric';
  }, [biometricType]);

  // ============================================================
  // FORGOT PASSWORD
  // ============================================================
  const openForgotPassword = useCallback(() => {
    setForgotStep(1);
    setForgotEmail('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setShowForgotPassword(true);
  }, []);

  const closeForgotPassword = useCallback(() => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotEmail('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
  }, []);

  const handleForgotStep1 = useCallback(async () => {
    const sanitizedEmail = sanitizeInput(forgotEmail);
    if (!sanitizedEmail || !validateEmail(sanitizedEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await capacitorDbService.init();
      const user = await capacitorDbService.getUserByEmail(sanitizedEmail.toLowerCase());

      if (!user) {
        showError('No account found with that email address');
        setIsLoading(false);
        return;
      }

      // Check if it's a social login account (can't reset)
      if (user.password?.startsWith('google_') || user.password?.startsWith('apple_')) {
        showError('This account uses social login. Please use Google or Apple sign-in instead.');
        closeForgotPassword();
        setIsLoading(false);
        return;
      }

      setForgotStep(2);
    } catch (error: any) {
      logger.error('Forgot password step 1 failed', error);
      showError('Unable to verify email. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [forgotEmail, showError, closeForgotPassword]);

  const handleForgotStep2 = useCallback(async () => {
    if (!forgotNewPassword || !forgotConfirmPassword) {
      showError('Please fill in both password fields');
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      showError('Passwords do not match');
      return;
    }

    const passwordCheck = validatePassword(forgotNewPassword);
    if (!passwordCheck.valid) {
      setValidationErrors(passwordCheck.errors);
      showError('Password does not meet requirements');
      return;
    }

    setIsLoading(true);
    try {
      await capacitorDbService.init();
      const user = await capacitorDbService.getUserByEmail(forgotEmail.toLowerCase());

      if (!user) {
        showError('Account not found. Please start over.');
        closeForgotPassword();
        setIsLoading(false);
        return;
      }

      const newHash = await hashPassword(forgotNewPassword);
      await capacitorDbService.updateUser(user.id, { password: newHash });

      closeForgotPassword();
      setEmail(forgotEmail);
      setPassword('');
      showError('Password reset successfully! Please sign in with your new password.');
    } catch (error: any) {
      logger.error('Password reset failed', error);
      showError('Failed to reset password. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [forgotEmail, forgotNewPassword, forgotConfirmPassword, showError, closeForgotPassword, setEmail]);

  // ============================================================
  // DESIGN: Color Palette
  // ============================================================
  const deepGreen = '#0B3E2E';
  const green = '#1B5A40';
  const lightGreen = '#22C55E';
  const gold = '#D4AF37';
  const cream = '#F5F5DC';
  const creamLight = '#FAF8F0';

  const handleDismissToast = useCallback(() => {
    setShowToast(false);
  }, []);

  return (
    <LoginErrorBoundary>
      <IonPage>
        <IonHeader className="ion-no-border" style={{ display: 'none' }}>
          <IonToolbar>
            <IonTitle>NurWallet</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent
          className="ion-no-padding"
          scrollY={true}
          style={{
            '--background': 'transparent',
            '--padding-start': '0px',
            '--padding-end': '0px',
            '--padding-top': '0px',
            '--padding-bottom': '0px'
          } as React.CSSProperties}
        >
          <div style={{
            minHeight: '100vh',
            background: `linear-gradient(180deg, ${creamLight} 0%, #FFFFFF 50%, ${creamLight} 100%)`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background Images — Lazy Loaded (below fold) */}
            <LazyImage
              src="./images/bg-mosque.png"
              alt=""
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.06,
                zIndex: 0,
                pointerEvents: 'none'
              }}
            />
            <LazyImage
              src="./images/bg-arch.png"
              alt=""
              style={{
                position: 'absolute',
                top: '-20px',
                left: '-20px',
                width: '220px',
                height: 'auto',
                opacity: 0.9,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />
            <LazyImage
              src="./images/bg-lantern.png"
              alt=""
              style={{
                position: 'absolute',
                top: '10px',
                right: '20px',
                width: '100px',
                height: 'auto',
                opacity: 0.95,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />
            <LazyImage
              src="./images/bg-leaves.png"
              alt=""
              style={{
                position: 'absolute',
                bottom: '180px',
                left: '-10px',
                width: '80px',
                height: 'auto',
                opacity: 0.85,
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />

            {/* Main Content */}
            <div style={{ position: 'relative', zIndex: 2, padding: '24px 20px 40px' }}>

              {/* Logo & Branding */}
              <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '20px' }}>
                {/* Logo — priority load (above fold) */}
                <div style={{ width: '80px', height: '80px', margin: '0 auto 12px' }}>
                  <LazyImage
                    src="./images/logo-nurwallet.png"
                    alt="NurWallet Logo"
                    priority
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </div>

                <h1 style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: deepGreen,
                  margin: '0 0 4px',
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '-0.5px'
                }}>
                  Nur<span style={{ color: gold, fontWeight: 400 }}>Wallet</span>
                </h1>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  marginBottom: '8px'
                }}>
                  <span style={{ color: gold, fontSize: '10px' }}>◆</span>
                  <span style={{
                    fontSize: '13px',
                    color: green,
                    fontWeight: 500,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase'
                  }}>
                    Islamic Digital Wallet
                  </span>
                  <span style={{ color: gold, fontSize: '10px' }}>◆</span>
                </div>

                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: deepGreen,
                  margin: '16px 0 8px',
                  fontFamily: 'Georgia, serif'
                }}>
                  {isRegistering ? 'Create Account' : 'Welcome Back'}
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  lineHeight: 1.5,
                  maxWidth: '280px',
                  margin: '0 auto'
                }}>
                  {isRegistering
                    ? `Join us to secure your family's future`
                    : `Secure your family's future with Shariah-compliant planning`}
                </p>
              </div>

              {/* Lockout Warning */}
              {lockoutMessage && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={18} color="#EF4444" />
                  <span style={{ fontSize: '13px', color: '#DC2626', fontWeight: 500 }}>
                    {lockoutMessage}
                  </span>
                </div>
              )}

              {/* Social Login */}
              {!isRegistering && !lockoutMessage && (
                <div style={{ marginBottom: '20px' }}>
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    aria-label="Sign in with Google"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      color: '#374151',
                      padding: '14px 16px',
                      borderRadius: '16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      marginBottom: '12px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.6 : 1,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <button
                    onClick={handleAppleSignIn}
                    disabled={isLoading}
                    aria-label="Sign in with Apple"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      background: '#000000',
                      color: '#FFFFFF',
                      padding: '14px 16px',
                      borderRadius: '16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.6 : 1,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.21-1.98 1.08-3.11-1.05.05-2.31.72-3.06 1.64-.67.78-1.26 2.02-1.1 3.12 1.17.09 2.37-.74 3.08-1.65"/>
                    </svg>
                    Continue with Apple
                  </button>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '20px 0'
                  }}>
                    <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #D4AF37)' }} />
                    <div style={{ width: '6px', height: '6px', background: gold, transform: 'rotate(45deg)', borderRadius: '1px' }} />
                    <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 500 }}>OR</span>
                    <div style={{ width: '6px', height: '6px', background: gold, transform: 'rotate(45deg)', borderRadius: '1px' }} />
                    <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, #D4AF37)' }} />
                  </div>
                </div>
              )}

              {/* Form Card */}
              <div style={{
                background: '#FFFFFF',
                borderRadius: '24px',
                padding: '24px',
                boxShadow: '0 4px 24px rgba(11, 62, 46, 0.08)',
                border: '1px solid rgba(212, 175, 55, 0.15)',
                marginBottom: '20px'
              }}>
                {isRegistering && (
                  <div style={{ marginBottom: '16px' }}>
                    <label 
                      htmlFor="fullName"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}
                    >
                      Full Name
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '14px',
                      padding: '0 14px',
                      transition: 'border-color 0.2s',
                      outline: 'none'
                    }}>
                      <User size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} aria-hidden="true" />
                      <input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={handleFullNameChange}
                        placeholder="Your full name"
                        autoComplete="name"
                        maxLength={100}
                        aria-required="true"
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          padding: '14px 0',
                          fontSize: '15px',
                          color: '#1F2937',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Email Field */}
                <div style={{ marginBottom: '16px' }}>
                  <label 
                    htmlFor="email"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '6px'
                    }}
                  >
                    Email Address
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#F9FAFB',
                    border: `1px solid ${email && !validateEmail(email) ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '14px',
                    padding: '0 14px',
                    transition: 'border-color 0.2s'
                  }}>
                    <Mail size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} aria-hidden="true" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="your@email.com"
                      autoComplete="email"
                      maxLength={255}
                      aria-required="true"
                      aria-invalid={email ? !validateEmail(email) : false}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        padding: '14px 0',
                        fontSize: '15px',
                        color: '#1F2937',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {email && !validateEmail(email) && (
                    <p style={{ fontSize: '12px', color: '#EF4444', margin: '4px 0 0' }}>
                      Please enter a valid email address
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div style={{ marginBottom: '16px' }}>
                  <label 
                    htmlFor="password"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '6px'
                    }}
                  >
                    Password
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#F9FAFB',
                    border: `1px solid ${validationErrors.length > 0 ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '14px',
                    padding: '0 14px',
                    transition: 'border-color 0.2s'
                  }}>
                    <Lock size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} aria-hidden="true" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder={isRegistering ? 'Min 8 characters' : 'Your password'}
                      autoComplete={isRegistering ? 'new-password' : 'current-password'}
                      maxLength={128}
                      aria-required="true"
                      aria-invalid={validationErrors.length > 0}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        padding: '14px 0',
                        fontSize: '15px',
                        color: '#1F2937',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="button"
                      onClick={togglePassword}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        color: '#9CA3AF'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>

                  {/* Password Requirements */}
                  {isRegistering && validationErrors.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {validationErrors.map((error, i) => (
                        <p key={i} style={{ fontSize: '12px', color: '#EF4444', margin: '2px 0' }}>
                          • {error}
                        </p>
                      ))}
                    </div>
                  )}

                  {isRegistering && password && validationErrors.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#22C55E', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Check size={14} /> Password meets all requirements
                    </p>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                {!isRegistering && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '20px'
                  }}>
                    <button
                      onClick={toggleRememberMe}
                      aria-label={rememberMe ? 'Disable remember me' : 'Enable remember me'}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '5px',
                        border: `2px solid ${rememberMe ? green : '#D1D5DB'}`,
                        background: rememberMe ? green : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}>
                        {rememberMe && <Check size={14} color="white" aria-hidden="true" />}
                      </div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                        Remember me
                      </span>
                    </button>
                    <button
                      onClick={openForgotPassword}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: green,
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {/* Biometric Login */}
                {!isRegistering && biometricEnabled && biometricAvailable && (
                  <button
                    onClick={handleBiometricLogin}
                    disabled={isLoading}
                    aria-label={`Login with ${getBiometricLabel()}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: `rgba(27, 90, 64, 0.08)`,
                      border: `1px solid rgba(27, 90, 64, 0.2)`,
                      color: green,
                      padding: '12px',
                      borderRadius: '14px',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '16px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    {biometricType === 'faceId' ? <Shield size={18} aria-hidden="true" /> : <Fingerprint size={18} aria-hidden="true" />}
                    Login with {getBiometricLabel()}
                  </button>
                )}

                {/* Submit Button */}
                <button
                  onClick={isRegistering ? handleRegister : handleLogin}
                  disabled={isLoading || !!lockoutMessage}
                  aria-label={isRegistering ? 'Create account' : 'Sign in'}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    background: `linear-gradient(135deg, ${deepGreen} 0%, ${green} 100%)`,
                    color: '#FFFFFF',
                    padding: '16px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: 600,
                    border: 'none',
                    cursor: isLoading || lockoutMessage ? 'not-allowed' : 'pointer',
                    opacity: isLoading || lockoutMessage ? 0.7 : 1,
                    boxShadow: `0 4px 16px rgba(11, 62, 46, 0.3)`,
                    transition: 'all 0.2s',
                    marginBottom: '16px'
                  }}
                >
                  <ArrowRight size={20} color={gold} aria-hidden="true" />
                  {isLoading ? 'Please wait...' : (isRegistering ? 'Create Account' : 'Sign In')}
                </button>

                {/* Toggle Register/Login */}
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={toggleRegister}
                    aria-label={isRegistering ? 'Switch to sign in' : 'Switch to create account'}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '14px',
                      color: '#6B7280',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    {isRegistering ? (
                      <>Already have an account? <span style={{ color: green, fontWeight: 600 }}>Sign In</span></>
                    ) : (
                      <>New to NurWallet? <span style={{ color: green, fontWeight: 600 }}>Create an account</span></>
                    )}
                  </button>
                </div>
              </div>

              {/* Guest Login */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <button
                  onClick={handleGuestLogin}
                  disabled={isLoading}
                  aria-label="Continue as guest"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.6 : 1,
                    padding: '8px 16px',
                    borderRadius: '12px',
                    transition: 'all 0.2s'
                  }}
                >
                  <User size={18} color={gold} aria-hidden="true" />
                  Continue as Guest
                  <ChevronRight size={16} color={gold} aria-hidden="true" />
                </button>
              </div>

              {/* Trust Badges */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(212, 175, 55, 0.15)'
                }}>
                  <TrustBadgeIcon src="./images/icon-shariah.png" alt="Shariah Compliant" />
                  <h4 style={{ fontSize: '12px', fontWeight: 700, color: deepGreen, margin: '0 0 4px' }}>
                    Shariah Compliant
                  </h4>
                  <p style={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.4, margin: 0 }}>
                    100% Shariah-compliant financial planning
                  </p>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(212, 175, 55, 0.15)'
                }}>
                  <TrustBadgeIcon src="./images/icon-secure.png" alt="Secure & Private" />
                  <h4 style={{ fontSize: '12px', fontWeight: 700, color: deepGreen, margin: '0 0 4px' }}>
                    Secure & Private
                  </h4>
                  <p style={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.4, margin: 0 }}>
                    Bank-level security to protect your data
                  </p>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(212, 175, 55, 0.15)'
                }}>
                  <TrustBadgeIcon src="./images/icon-trusted.png" alt="Trusted & Ethical" />
                  <h4 style={{ fontSize: '12px', fontWeight: 700, color: deepGreen, margin: '0 0 4px' }}>
                    Trusted & Ethical
                  </h4>
                  <p style={{ fontSize: '10px', color: '#6B7280', lineHeight: 1.4, margin: 0 }}>
                    Built on Islamic values of trust and transparency
                  </p>
                </div>
              </div>

              {/* Support Footer */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => {/* TODO: Support flow */}}
                  aria-label="Contact support"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    color: green,
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '8px 16px'
                  }}
                >
                  <Headphones size={18} color={gold} aria-hidden="true" />
                  Need help? Contact Support
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(11, 62, 46, 0.6)',
              backdropFilter: 'blur(8px)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <div style={{
                background: '#FFFFFF',
                borderRadius: '24px',
                padding: '28px',
                width: '100%',
                maxWidth: '400px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                border: '1px solid rgba(212, 175, 55, 0.2)'
              }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: `linear-gradient(135deg, ${deepGreen} 0%, ${green} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px'
                  }}>
                    <KeyRound size={28} color="#FFFFFF" />
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: deepGreen,
                    margin: '0 0 6px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {forgotStep === 1 ? 'Forgot Password?' : 'Reset Password'}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
                    {forgotStep === 1
                      ? 'Enter your email to verify your account'
                      : 'Enter your new password below'}
                  </p>
                </div>

                {/* Step 1: Email Verification */}
                {forgotStep === 1 && (
                  <div>
                    <label
                      htmlFor="forgotEmail"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px'
                      }}
                    >
                      Email Address
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '14px',
                      padding: '0 14px',
                      marginBottom: '20px'
                    }}>
                      <Mail size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} />
                      <input
                        id="forgotEmail"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(sanitizeInput(e.target.value))}
                        placeholder="your@email.com"
                        autoComplete="email"
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          padding: '14px 0',
                          fontSize: '15px',
                          color: '#1F2937',
                          outline: 'none'
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleForgotStep1(); }}
                      />
                    </div>

                    <button
                      onClick={handleForgotStep1}
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        background: `linear-gradient(135deg, ${deepGreen} 0%, ${green} 100%)`,
                        color: '#FFFFFF',
                        padding: '16px',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.7 : 1,
                        boxShadow: `0 4px 16px rgba(11, 62, 46, 0.3)`,
                        marginBottom: '12px'
                      }}
                    >
                      <ArrowRight size={20} color={gold} />
                      {isLoading ? 'Verifying...' : 'Verify Email'}
                    </button>
                  </div>
                )}

                {/* Step 2: New Password */}
                {forgotStep === 2 && (
                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <label
                        htmlFor="forgotNewPassword"
                        style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '6px'
                        }}
                      >
                        New Password
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '14px',
                        padding: '0 14px'
                      }}>
                        <Lock size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} />
                        <input
                          id="forgotNewPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={forgotNewPassword}
                          onChange={(e) => setForgotNewPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          autoComplete="new-password"
                          style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            padding: '14px 0',
                            fontSize: '15px',
                            color: '#1F2937',
                            outline: 'none'
                          }}
                        />
                        <button
                          type="button"
                          onClick={togglePassword}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            color: '#9CA3AF'
                          }}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label
                        htmlFor="forgotConfirmPassword"
                        style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                          marginBottom: '6px'
                        }}
                      >
                        Confirm Password
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '14px',
                        padding: '0 14px'
                      }}>
                        <Lock size={18} color={green} style={{ marginRight: '10px', flexShrink: 0 }} />
                        <input
                          id="forgotConfirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={forgotConfirmPassword}
                          onChange={(e) => setForgotConfirmPassword(e.target.value)}
                          placeholder="Re-enter your password"
                          autoComplete="new-password"
                          style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            padding: '14px 0',
                            fontSize: '15px',
                            color: '#1F2937',
                            outline: 'none'
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleForgotStep2(); }}
                        />
                      </div>

                      {/* Password Requirements */}
                      {forgotNewPassword && forgotNewPassword !== forgotConfirmPassword && forgotConfirmPassword && (
                        <p style={{ fontSize: '12px', color: '#EF4444', margin: '4px 0 0' }}>
                          Passwords do not match
                        </p>
                      )}
                    </div>

                    <button
                      onClick={handleForgotStep2}
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        background: `linear-gradient(135deg, ${deepGreen} 0%, ${green} 100%)`,
                        color: '#FFFFFF',
                        padding: '16px',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.7 : 1,
                        boxShadow: `0 4px 16px rgba(11, 62, 46, 0.3)`,
                        marginBottom: '12px'
                      }}
                    >
                      <ArrowRight size={20} color={gold} />
                      {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                )}

                {/* Cancel */}
                <button
                  onClick={closeForgotPassword}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: 'none',
                    border: '1px solid #E5E7EB',
                    color: '#6B7280',
                    padding: '14px',
                    borderRadius: '16px',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <IonLoading isOpen={isLoading} message="Please wait..." />
          <IonToast
            isOpen={showToast}
            onDidDismiss={handleDismissToast}
            message={toastMessage}
            duration={3000}
            position="top"
            color={toastMessage.includes('✅') ? 'success' : 'danger'}
          />
        </IonContent>
      </IonPage>
    </LoginErrorBoundary>
  );
};

export default LoginScreen;